/**
 * TradeReplay — Deterministic trade replay engine
 * - Full context from bar 0, reveals candles one by one
 * - Price line labels float on LEFT side of chart (no right-axis clutter)
 * - Initial view: preloaded candles end at center of screen
 * - Outcome detection: SL hit / Target hit badges
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, CandlestickSeries, HistogramSeries, CrosshairMode } from 'lightweight-charts';
import { Play, Pause, SkipForward, RotateCcw, Gauge, CheckCircle, XCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { formatPrice, formatDateTime } from '../../utils/formatters';

const REPLAY_SPEEDS = [
  { value: 2000, label: '0.5x' },
  { value: 1000, label: '1x' },
  { value: 500,  label: '2x' },
  { value: 250,  label: '4x' },
  { value: 100,  label: '10x' },
];

const fmtVol = (v) => {
  if (!v) return '0';
  if (v >= 1e7) return (v / 1e7).toFixed(1) + ' Cr';
  if (v >= 1e5) return (v / 1e5).toFixed(1) + ' L';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + ' K';
  return String(Math.round(v));
};

const fmtEpochIST = (epochSec) => {
  if (!epochSec) return '—';
  try {
    return formatDateTime(epochSec * 1000);
  } catch { return '—'; }
};

const fmtPrice = (p) => formatPrice(p);

const IST_OFFSET = 19800;

const TradeReplay = ({
  symbol,
  entryDate,
  exitDate,
  entryPrice,
  exitPrice,
  stopLoss,
  targetPrice,
  productType = null,
  staticMode = false,
  side = 'BUY',
  height = 350,
  onComplete,
  className = '',
  // ACT scenario props
  candles: externalCandles = null,   // pre-loaded candle array — skips API fetch
  startFromIndex = null,             // replay starts from this index (rest shown static)
  onPauseAt = null,                  // callback fired when replay reaches startFromIndex
  hideOutcome = false,               // hide Mkt Close / P&L / Duration columns (ACT question phase)
  exitReason = null,                 // 'TARGET' | 'SL' | 'MANUAL' — overrides candle-scan outcome detection
}) => {
  const { colors, theme } = useTheme();

  const chartContainerRef = useRef(null);
  const chartRef          = useRef(null);
  const candleSeriesRef   = useRef(null);
  const volumeSeriesRef   = useRef(null);
  const entryLineRef      = useRef(null);
  const exitLineRef       = useRef(null);
  const slLineRef         = useRef(null);
  const targetLineRef     = useRef(null);
  const dayCloseLineRef   = useRef(null);
  const entryIdxRef       = useRef(0);
  const startIdxRef       = useRef(0);
  const replayIntervalRef = useRef(null);

  const [allCandles, setAllCandles]               = useState([]);
  const [currentIndex, setCurrentIndex]           = useState(0);
  const [displayedCandles, setDisplayedCandles]   = useState([]);
  const [isPlaying, setIsPlaying]                 = useState(false);
  const [isFinished, setIsFinished]               = useState(false);
  const [tradeOutcome, setTradeOutcome]           = useState(null);
  const [speed, setSpeed]                         = useState(500);
  const [loading, setLoading]                     = useState(true);
  const [error, setError]                         = useState(null);
  const [crosshairData, setCrosshairData]         = useState(null);
  // Floating price-line labels: [{label, color, y}]
  const [priceLabelPositions, setPriceLabelPositions] = useState([]);
  const [showSpeedMenu, setShowSpeedMenu]         = useState(false);
  const speedMenuRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
  const isDark  = theme === 'dark';

  const chartColors = {
    background: isDark ? '#0f172a' : '#ffffff',
    text:       isDark ? '#94a3b8' : '#64748b',
    grid:       isDark ? '#1e293b' : '#f1f5f9',
    upColor:    '#16C784',
    downColor:  '#ef4444',
    volumeUp:   'rgba(34, 197, 94, 0.55)',
    volumeDown: 'rgba(239, 68, 68, 0.55)',
    crosshair:  isDark ? '#475569' : '#94a3b8',
  };

  const isIntraday =
    productType === 'INTRADAY' ||
    (productType == null && exitDate != null &&
      entryDate?.slice(0, 10) === exitDate?.slice(0, 10));

  const effectiveExitPrice =
    exitPrice != null
      ? exitPrice
      : (isIntraday && allCandles.length > 0
          ? allCandles[allCandles.length - 1]?.close
          : null);

  // ── Recompute floating label Y positions (called after any chart update) ──
  const updateLabelPositions = useCallback(() => {
    if (!candleSeriesRef.current) return;
    const effExit =
      exitPrice != null
        ? exitPrice
        : (isIntraday && allCandles.length > 0
            ? allCandles[allCandles.length - 1]?.close
            : null);

    const dayClose = (isIntraday && exitPrice != null && allCandles.length > 0)
      ? allCandles[allCandles.length - 1]?.close : null;

    const pairs = [
      { label: 'ENTRY',                      price: entryPrice,  color: '#6366F1' },
      { label: exitPrice != null ? 'EXIT' : 'MKT CLOSE', price: effExit, color: '#f59e0b' },
      { label: 'SL',                          price: stopLoss,    color: '#ef4444' },
      { label: 'TARGET',                      price: targetPrice, color: '#16C784' },
      { label: 'DAY CLOSE',                   price: dayClose,    color: '#a78bfa' },
    ];

    const positions = [];
    pairs.forEach(({ label, price, color }) => {
      if (price == null) return;
      try {
        const y = candleSeriesRef.current.priceToCoordinate(price);
        if (y != null && y > 4 && y < height - 4) {
          positions.push({ label, color, y: Math.round(y) });
        }
      } catch (_) {}
    });
    setPriceLabelPositions(positions);
  }, [entryPrice, exitPrice, stopLoss, targetPrice, isIntraday, allCandles, height]);

  // ── Fetch candles ──────────────────────────────────────────────────────────
  const fetchReplayData = useCallback(async () => {
    if (!symbol || !entryDate) return;

    setLoading(true);
    setError(null);
    // Seed outcome from exitReason so candle-scan doesn't override a known result
    if (exitReason === 'TARGET') setTradeOutcome('SUCCESS');
    else if (exitReason === 'SL' || exitReason === 'STOP_LOSS') setTradeOutcome('FAILED');
    else setTradeOutcome(null);
    setIsFinished(false);
    setPriceLabelPositions([]);
    entryLineRef.current    = null;
    exitLineRef.current     = null;
    slLineRef.current       = null;
    targetLineRef.current   = null;
    dayCloseLineRef.current = null;

    try {
      const type = isIntraday ? 'intraday' : 'swing';
      let fromParam, toParam;

      if (isIntraday) {
        const d = new Date(entryDate);
        const dayStart = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 3, 45, 0));
        const dayEnd   = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 10, 0, 0));
        fromParam = encodeURIComponent(dayStart.toISOString());
        toParam   = `&to=${encodeURIComponent(dayEnd.toISOString())}`;
      } else {
        fromParam = encodeURIComponent(entryDate);
        toParam   = exitDate ? `&to=${encodeURIComponent(exitDate)}` : '';
      }

      const res = await fetch(
        `${API_URL}/trade/replay-data/${symbol}?from_=${fromParam}${toParam}&type=${type}`
      );
      if (!res.ok) throw new Error('Failed to fetch replay data');

      const data = await res.json();
      if (data.error) { setError(data.error); return; }

      let candles = data.candles || [];

      // Fallback for sim trades (real clock entryDate ≠ sim candle date)
      if (isIntraday && candles.length > 0 && candles.length <= 10) {
        const ld = new Date(candles[candles.length - 1].time * 1000);
        const fbStart = new Date(Date.UTC(ld.getUTCFullYear(), ld.getUTCMonth(), ld.getUTCDate(), 3, 45, 0));
        const fbEnd   = new Date(fbStart.getTime() + 6.25 * 3600000);
        const r2 = await fetch(
          `${API_URL}/trade/replay-data/${symbol}?from_=${encodeURIComponent(fbStart.toISOString())}&to=${encodeURIComponent(fbEnd.toISOString())}&type=intraday`
        );
        if (r2.ok) {
          const d2 = await r2.json();
          if (d2.candles?.length > candles.length) candles = d2.candles;
        }
      }

      if (!candles.length) { setError('No candle data available'); return; }
      if (candles.length < 5) { setError('Candle data not yet available — replay will work once backfill runs post-market'); return; }

      setAllCandles(candles);

      if (staticMode) {
        setDisplayedCandles(candles);
        setCurrentIndex(candles.length - 1);
        return;
      }

      // ── Locate entry candle ──────────────────────────────────────────────
      let entryIdx = 0;
      if (isIntraday) {
        const entryEpoch = Math.floor(new Date(entryDate).getTime() / 1000);
        let found = candles.findIndex(c => c.time >= entryEpoch - 60);
        const dayDiff = found >= 0 ? Math.abs(candles[found].time - entryEpoch) : Infinity;
        // Price-proximity fallback for sim trades (clock skew > 1h)
        if (found < 0 || dayDiff > 3600) {
          if (entryPrice != null) {
            let minDiff = Infinity;
            candles.forEach((c, i) => {
              const diff = Math.abs(c.close - entryPrice);
              if (diff < minDiff) { minDiff = diff; found = i; }
            });
          }
        }
        entryIdx = Math.max(0, found >= 0 ? found : 0);
      } else {
        const str   = entryDate.slice(0, 10);
        const found = candles.findIndex(c => String(c.time) >= str);
        entryIdx = found >= 0 ? found : 0;
      }

      // Preload all candles up to entry so the left side is filled
      const startIdx = entryIdx;
      entryIdxRef.current = entryIdx;
      startIdxRef.current = startIdx;

      setDisplayedCandles(candles.slice(0, startIdx + 1));
      setCurrentIndex(startIdx);
    } catch (err) {
      console.error('Replay fetch error:', err);
      setError('Failed to load replay data');
    } finally {
      setLoading(false);
    }
  }, [symbol, entryDate, exitDate, isIntraday, entryPrice, staticMode, API_URL]); // eslint-disable-line

  // ── Initialize chart ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width:  chartContainerRef.current.clientWidth,
      height,
      layout: {
        background: { type: 'solid', color: chartColors.background },
        textColor:  chartColors.text,
      },
      grid: {
        vertLines: { color: chartColors.grid },
        horzLines: { color: chartColors.grid },
      },
      crosshair: {
        mode:     CrosshairMode.Normal,
        vertLine: { color: chartColors.crosshair, width: 1, visible: true },
        horzLine: { color: chartColors.crosshair, width: 1, visible: true },
      },
      rightPriceScale: { borderColor: chartColors.grid },
      timeScale: {
        borderColor:    chartColors.grid,
        timeVisible:    isIntraday,
        secondsVisible: false,
        // rightOffset: leave some empty space so latest candle sits ~center
        rightOffset: 40,
      },
      localization: {
        locale:     'en-IN',
        dateFormat: 'dd MMM yyyy',
        timeFormatter: (ts) => {
          const d = new Date(ts * 1000);
          return `${String(d.getUTCHours()).padStart(2,'0')}:${String(d.getUTCMinutes()).padStart(2,'0')}`;
        },
      },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor:        chartColors.upColor,
      downColor:      chartColors.downColor,
      borderDownColor: chartColors.downColor,
      borderUpColor:  chartColors.upColor,
      wickDownColor:  chartColors.downColor,
      wickUpColor:    chartColors.upColor,
    });

    const volumeSeries = chart.addSeries(HistogramSeries, {
      color:        chartColors.volumeUp,
      priceFormat:  { type: 'volume' },
      priceScaleId: 'volume',
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
      visible: false,
    });

    chart.subscribeCrosshairMove((param) => {
      if (param.time && param.seriesData.size > 0) {
        const cd = param.seriesData.get(candleSeries);
        const vd = param.seriesData.get(volumeSeries);
        if (cd) setCrosshairData({ ...cd, volume: vd?.value ?? 0 });
      } else {
        setCrosshairData(null);
      }
    });

    // Recompute label Y positions whenever the visible range changes (pan/zoom)
    chart.timeScale().subscribeVisibleLogicalRangeChange(() => {
      requestAnimationFrame(() => updateLabelPositions());
    });

    chartRef.current      = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    const onResize = () => {
      if (chartContainerRef.current)
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      requestAnimationFrame(() => updateLabelPositions());
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      chart.remove();
      entryLineRef.current    = null;
      exitLineRef.current     = null;
      slLineRef.current       = null;
      targetLineRef.current   = null;
      dayCloseLineRef.current = null;
    };
  }, [theme, height]); // eslint-disable-line

  useEffect(() => {
    if (externalCandles && externalCandles.length > 0) {
      setAllCandles(externalCandles);
      setLoading(false);
      setError(null);
      setIsFinished(false);
      setTradeOutcome(null);
      const sIdx = startFromIndex != null
        ? Math.min(startFromIndex, externalCandles.length - 1)
        : externalCandles.length - 1;
      entryIdxRef.current = sIdx;
      startIdxRef.current = sIdx;
      setDisplayedCandles(externalCandles.slice(0, sIdx + 1));
      setCurrentIndex(sIdx);
    } else {
      fetchReplayData();
    }
  }, [externalCandles, startFromIndex, fetchReplayData]); // eslint-disable-line

  // ── Update chart when displayedCandles changes ─────────────────────────────
  useEffect(() => {
    if (!candleSeriesRef.current || !displayedCandles.length) return;

    // Normalize time: intraday needs epoch int; swing needs 'YYYY-MM-DD' string.
    // Backend may store either format so we handle both defensively.
    const toTime = (t) => {
      if (isIntraday) {
        const epoch = typeof t === 'number' ? t : Math.floor(new Date(t).getTime() / 1000);
        return epoch + IST_OFFSET;
      }
      return typeof t === 'string' ? t : new Date(t * 1000).toISOString().slice(0, 10);
    };

    const fmtC = displayedCandles.map(c => ({
      time:  toTime(c.time),
      open:  c.open, high: c.high, low: c.low, close: c.close,
    }));

    candleSeriesRef.current.setData(fmtC);

    if (volumeSeriesRef.current) {
      volumeSeriesRef.current.setData(displayedCandles.map(c => ({
        time:  toTime(c.time),
        value: c.volume,
        color: c.close >= c.open ? chartColors.volumeUp : chartColors.volumeDown,
      })));
    }

    // ── Markers (index-based, immune to timestamp skew) ──────────────────
    try {
      const markers = [];
      const ei = entryIdxRef.current;

      if (ei < fmtC.length) {
        markers.push({
          time:     fmtC[ei].time,
          position: 'belowBar',
          color:    '#6366F1',
          shape:    'arrowUp',
          text:     'Entry',
        });
      }
      if (exitDate && exitPrice != null) {
        let ec;
        if (isIntraday) {
          const epoch = Math.floor(new Date(exitDate).getTime() / 1000) + IST_OFFSET;
          ec = [...fmtC].reverse().find(c => c.time <= epoch);
        } else {
          const str = exitDate.slice(0, 10);
          ec = [...fmtC].reverse().find(c => String(c.time) <= str);
        }
        if (ec) markers.push({ time: ec.time, position: 'aboveBar', color: '#f59e0b', shape: 'arrowDown', text: 'Exit' });
      }
      if (markers.length) candleSeriesRef.current.setMarkers(markers);
    } catch (_) {}

    // ── Scroll: anchor last candle at right with ~15-bar window; replay scrolls live ──
    if (chartRef.current) {
      const isInitialLoad = displayedCandles.length - 1 <= startIdxRef.current;
      if (staticMode || isInitialLoad) {
        // Show last ~15 candles so the signal day is prominent at the right edge
        const total = displayedCandles.length;
        const visibleBars = Math.min(15, total);
        chartRef.current.timeScale().setVisibleLogicalRange({
          from: total - visibleBars,
          to:   total + 2, // small right padding
        });
      } else {
        chartRef.current.timeScale().scrollToRealTime();
      }
    }

    // Update floating label Y positions after chart renders
    requestAnimationFrame(() => updateLabelPositions());
  }, [displayedCandles]); // eslint-disable-line

  // ── Create price lines (no axis labels — we use floating HTML labels) ──────
  useEffect(() => {
    if (!candleSeriesRef.current || !allCandles.length) return;

    const effExit =
      exitPrice != null
        ? exitPrice
        : (isIntraday && allCandles.length > 0 ? allCandles[allCandles.length - 1]?.close : null);

    try {
      if (entryPrice != null && !entryLineRef.current) {
        entryLineRef.current = candleSeriesRef.current.createPriceLine({
          price: entryPrice, color: '#6366F1', lineWidth: 2,
          lineStyle: 0, axisLabelVisible: false,
        });
      }
      if (effExit != null && !exitLineRef.current) {
        exitLineRef.current = candleSeriesRef.current.createPriceLine({
          price: effExit, color: '#f59e0b', lineWidth: 2,
          lineStyle: exitPrice != null ? 0 : 2,
          axisLabelVisible: false,
        });
      }
      if (stopLoss != null) {
        if (slLineRef.current) {
          slLineRef.current.applyOptions({ price: stopLoss });
        } else {
          slLineRef.current = candleSeriesRef.current.createPriceLine({
            price: stopLoss, color: '#ef4444', lineWidth: 2,
            lineStyle: 0, axisLabelVisible: false,
          });
        }
      } else if (slLineRef.current) {
        candleSeriesRef.current.removePriceLine(slLineRef.current);
        slLineRef.current = null;
      }
      if (targetPrice != null) {
        if (targetLineRef.current) {
          targetLineRef.current.applyOptions({ price: targetPrice });
        } else {
          targetLineRef.current = candleSeriesRef.current.createPriceLine({
            price: targetPrice, color: '#16C784', lineWidth: 2,
            lineStyle: 0, axisLabelVisible: false,
          });
        }
      } else if (targetLineRef.current) {
        candleSeriesRef.current.removePriceLine(targetLineRef.current);
        targetLineRef.current = null;
      }
      // Day close line — only for intraday trades that have a real exit price
      const dayClose = (isIntraday && exitPrice != null && allCandles.length > 0)
        ? allCandles[allCandles.length - 1]?.close : null;
      if (dayClose != null && dayClose !== exitPrice && !dayCloseLineRef.current) {
        dayCloseLineRef.current = candleSeriesRef.current.createPriceLine({
          price: dayClose, color: '#a78bfa', lineWidth: 1,
          lineStyle: 2, axisLabelVisible: false,
        });
      }
    } catch (e) {
      console.log('Price line:', e.message);
    }

    requestAnimationFrame(() => {
      if (chartRef.current) chartRef.current.timeScale().fitContent();
      updateLabelPositions();
    });
  }, [allCandles, entryPrice, exitPrice, stopLoss, targetPrice, isIntraday]); // eslint-disable-line

  // ── Replay controls ────────────────────────────────────────────────────────
  const advanceCandle = useCallback(() => {
    setCurrentIndex(prev => {
      if (prev >= allCandles.length - 1) {
        setIsPlaying(false);
        setIsFinished(true);
        if (onComplete) onComplete();
        return prev;
      }
      const next      = prev + 1;
      const newCandle = allCandles[next];

      // ACT: pause at startFromIndex boundary so question overlay can show
      if (startFromIndex != null && next >= startFromIndex && prev < startFromIndex) {
        setIsPlaying(false);
        if (onPauseAt) onPauseAt();
        setDisplayedCandles(allCandles.slice(0, next + 1));
        return next;
      }

      // Only infer outcome from candles when exitReason isn't already known
      const knownOutcome = exitReason === 'TARGET' || exitReason === 'SL' || exitReason === 'STOP_LOSS';
      if (!knownOutcome && next >= entryIdxRef.current && newCandle) {
        setTradeOutcome(cur => {
          if (cur) return cur;
          if (targetPrice != null && newCandle.high >= targetPrice) {
            setIsPlaying(false);
            return 'SUCCESS';
          }
          if (stopLoss != null && newCandle.low <= stopLoss) {
            setIsPlaying(false);
            return 'FAILED';
          }
          return cur;
        });
      }

      setDisplayedCandles(allCandles.slice(0, next + 1));
      return next;
    });
  }, [allCandles, onComplete, targetPrice, stopLoss, startFromIndex, onPauseAt]); // eslint-disable-line

  useEffect(() => {
    if (isPlaying) replayIntervalRef.current = setInterval(advanceCandle, speed);
    else clearInterval(replayIntervalRef.current);
    return () => clearInterval(replayIntervalRef.current);
  }, [isPlaying, speed, advanceCandle]);

  useEffect(() => {
    if (!showSpeedMenu) return;
    const onClickOutside = (e) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(e.target)) setShowSpeedMenu(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showSpeedMenu]);

  const handlePlayPause = () => (isPlaying ? setIsPlaying(false) : (!isFinished && setIsPlaying(true)));
  const handleNext      = () => { setIsPlaying(false); advanceCandle(); };
  const handleReset     = () => {
    setIsPlaying(false);
    setIsFinished(false);
    setTradeOutcome(null);
    const s = startIdxRef.current;
    setCurrentIndex(s);
    if (allCandles.length) setDisplayedCandles(allCandles.slice(0, s + 1));
  };

  const progress = allCandles.length > 1
    ? Math.round((currentIndex / (allCandles.length - 1)) * 100)
    : 0;

  // Duration from candle timestamps (immune to sim clock skew)
  const getDuration = useCallback(() => {
    const eC = allCandles[entryIdxRef.current];
    const lC = allCandles[allCandles.length - 1];
    if (!eC || !lC) return null;
    // candle.time can be a date string ("YYYY-MM-DD") for daily candles or an epoch int for intraday
    const toMs = (t) => typeof t === 'string' ? new Date(t).getTime() : t * 1000;
    const ms = toMs(lC.time) - toMs(eC.time);
    if (!ms || ms <= 0) return null;
    const mins = Math.floor(ms / 60000);
    const hrs  = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);
    if (days > 0) return `${days}d`;
    if (hrs > 0)  return `${hrs}h ${mins % 60}m`;
    return `${mins}m`;
  }, [allCandles]);

  const effExit = effectiveExitPrice;
  const pnlPct  = effExit != null && entryPrice != null
    ? (side === 'BUY'
        ? (effExit - entryPrice) / entryPrice * 100
        : (entryPrice - effExit) / entryPrice * 100
      ).toFixed(2)
    : null;
  const pnlPositive = pnlPct !== null && parseFloat(pnlPct) >= 0;
  const rrRatio = stopLoss != null && targetPrice != null && entryPrice != null
    ? Math.abs((targetPrice - entryPrice) / (entryPrice - stopLoss))
    : null;

  const entryCandle = allCandles[entryIdxRef.current];
  const entryTimeDisplay = entryCandle ? fmtEpochIST(entryCandle.time) : '—';
  const exitTimeDisplay  = exitDate
    ? (() => {
        try {
          return formatDateTime(exitDate);
        } catch { return '—'; }
      })()
    : (isIntraday ? '15:30 IST' : '—');

  return (
    <div className={`rounded-xl overflow-hidden ${className}`}
         style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-3 py-2 border-b"
           style={{ borderColor: colors.border }}>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-sm" style={{ color: colors.primaryText }}>
            Trade Replay: {symbol?.replace('.NS', '')}
          </h3>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            side === 'BUY' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
          }`}>{side}</span>
          <span className="text-[13px] px-2 py-0.5 rounded font-medium"
                style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>
            {isIntraday ? '1-min' : 'Daily'}
          </span>
          {/* Mkt Close badge in header */}
          {effExit != null && exitPrice == null && (
            <span className="text-[13px] px-2 py-0.5 rounded font-medium"
                  style={{ backgroundColor: '#f59e0b22', color: '#f59e0b' }}>
              Mkt Close {fmtPrice(effExit)}
            </span>
          )}
          {tradeOutcome === 'SUCCESS' && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-green-500/20 text-green-400">
              <CheckCircle className="w-3 h-3" /><span>Target Hit</span>
            </span>
          )}
          {tradeOutcome === 'FAILED' && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-red-500/20 text-red-400">
              <XCircle className="w-3 h-3" /><span>SL Hit</span>
            </span>
          )}
        </div>
        {!staticMode && (
          <span className="text-xs shrink-0" style={{ color: colors.secondaryText }}>
            {currentIndex + 1} / {allCandles.length} ({progress}%)
          </span>
        )}
      </div>

      {/* ── Controls ── */}
      {!staticMode && (
        <div className="flex items-center justify-between px-3 py-2 border-b"
             style={{ borderColor: colors.border }}>
          <div className="flex items-center space-x-2">
            <button onClick={handlePlayPause} className="p-1.5 rounded-lg"
                    style={{ backgroundColor: colors.inputBg }} data-testid="replay-play-pause">
              {isPlaying
                ? <Pause className="w-4 h-4" style={{ color: colors.primaryText }} />
                : <Play  className="w-4 h-4" style={{ color: colors.primaryText }} />}
            </button>
            <button onClick={handleNext} disabled={currentIndex >= allCandles.length - 1}
                    className="p-1.5 rounded-lg disabled:opacity-40"
                    style={{ backgroundColor: colors.inputBg }} data-testid="replay-next">
              <SkipForward className="w-4 h-4" style={{ color: colors.primaryText }} />
            </button>
            <button onClick={handleReset} className="p-1.5 rounded-lg"
                    style={{ backgroundColor: colors.inputBg }} data-testid="replay-reset">
              <RotateCcw className="w-4 h-4" style={{ color: colors.primaryText }} />
            </button>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 text-[13px]">
            {[
              { label: 'Entry',     color: '#6366F1', show: true },
              { label: exitPrice != null ? 'Exit' : 'Mkt Close', color: '#f59e0b', show: effExit != null },
              { label: 'SL',       color: '#ef4444', show: stopLoss    != null },
              { label: 'Target',   color: '#16C784', show: targetPrice != null },
              { label: 'Day Close', color: '#a78bfa', show: isIntraday && exitPrice != null && allCandles.length > 0 },
            ].filter(x => x.show).map(({ label, color }) => (
              <span key={label} className="flex items-center gap-1">
                <span className="w-3 h-0.5 inline-block rounded" style={{ backgroundColor: color }} />
                <span style={{ color: colors.secondaryText }}>{label}</span>
              </span>
            ))}
          </div>

          {/* Speed */}
          <div className="relative" ref={speedMenuRef}>
            <button onClick={() => setShowSpeedMenu(v => !v)}
                    className="flex items-center gap-1 px-1.5 py-0.5 text-[13px] rounded"
                    style={{ color: colors.secondaryText }}
                    data-testid="replay-speed-toggle">
              <Gauge className="w-3.5 h-3.5" />
              <span>{REPLAY_SPEEDS.find(s => s.value === speed)?.label || '1x'}</span>
            </button>
            {showSpeedMenu && (
              <div className="absolute bottom-full right-0 mb-1 rounded-lg shadow-lg overflow-hidden z-10"
                   style={{ backgroundColor: colors.cardBg || colors.background, border: `1px solid ${colors.grid}` }}>
                {REPLAY_SPEEDS.map(({ value, label }) => (
                  <button key={value}
                          onClick={() => { setSpeed(value); setShowSpeedMenu(false); }}
                          className="block w-full px-3 py-1.5 text-[13px] text-right whitespace-nowrap"
                          style={{
                            backgroundColor: speed === value ? colors.accent : 'transparent',
                            color: speed === value ? '#fff' : colors.secondaryText,
                          }}
                          data-testid={`replay-speed-${label}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="h-0.5" style={{ backgroundColor: colors.grid }}>
        <div className="h-full transition-all duration-100"
             style={{ width: `${progress}%`, backgroundColor: colors.accent }} />
      </div>

      {/* ── Chart area ── */}
      <div className="relative" style={{ height: `${height}px` }}>
        {loading && (
          <div className="app-card absolute inset-0 flex items-center justify-center z-10"
               style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
            <div className="w-6 h-6 border-2 rounded-full animate-spin"
                 style={{ borderColor: colors.accent, borderTopColor: 'transparent' }} />
          </div>
        )}
        {error && !loading && (
          <div className="app-card absolute inset-0 flex items-center justify-center z-10"
               style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
            <p className="text-sm" style={{ color: '#ef4444' }}>{error}</p>
          </div>
        )}

        {/* OHLCV crosshair tooltip */}
        {crosshairData && (
          <div className="absolute top-2 left-2 z-20 pointer-events-none flex items-center space-x-2 text-[13px] rounded px-2 py-1"
               style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.72)' : 'rgba(15,23,42,0.82)' }}>
            <span style={{ color: crosshairData.close >= crosshairData.open ? '#16C784' : '#ef4444' }}>
              O {crosshairData.open?.toFixed(2)}&nbsp;
              H {crosshairData.high?.toFixed(2)}&nbsp;
              L {crosshairData.low?.toFixed(2)}&nbsp;
              C {crosshairData.close?.toFixed(2)}
            </span>
            <span style={{ color: '#94a3b8' }}>Vol {fmtVol(crosshairData.volume)}</span>
          </div>
        )}

        {/* ── Floating price line labels (LEFT side) ── */}
        {!loading && priceLabelPositions.map(({ label, color, y }) => (
          <div key={label}
               className="absolute pointer-events-none z-10 flex items-center"
               style={{ top: y - 9, left: 6 }}>
            <span className="text-[12px] font-bold px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: `${color}28`,
                    color,
                    border: `1px solid ${color}55`,
                    backdropFilter: 'blur(2px)',
                  }}>
              {label}
            </span>
          </div>
        ))}

        <div ref={chartContainerRef} data-testid="replay-chart-container"
             style={{ width: '100%', height: '100%' }} />
      </div>

      {/* ── Professional Info Bar ── */}
      <div className="px-3 py-3 border-t" style={{ borderColor: colors.border }}>
        <div className={`grid ${hideOutcome ? 'grid-cols-1' : 'grid-cols-4'} gap-3`}>

          <div>
            <p className="text-[12px] uppercase tracking-wider font-semibold mb-1"
               style={{ color: colors.secondaryText }}>Entry</p>
            <p className="text-xs font-bold" style={{ color: '#6366F1' }}>{fmtPrice(entryPrice)}</p>
            <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>{entryTimeDisplay}</p>
          </div>

          {!hideOutcome && (<>
            <div>
              <p className="text-[12px] uppercase tracking-wider font-semibold mb-1"
                 style={{ color: colors.secondaryText }}>
                {exitPrice != null ? 'Exit' : 'Mkt Close'}
              </p>
              <p className="text-xs font-bold" style={{ color: effExit != null ? '#f59e0b' : colors.secondaryText }}>
                {fmtPrice(effExit)}
                {exitPrice == null && effExit != null && (
                  <span className="ml-1 text-[11px] font-normal" style={{ color: colors.secondaryText }}>est.</span>
                )}
              </p>
              <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>{exitTimeDisplay}</p>
            </div>

            <div>
              <p className="text-[12px] uppercase tracking-wider font-semibold mb-1"
                 style={{ color: colors.secondaryText }}>P&amp;L</p>
              {pnlPct !== null ? (
                <>
                  <p className="text-xs font-bold"
                     style={{ color: pnlPositive ? '#16C784' : '#ef4444' }}>
                    {pnlPositive ? '+' : ''}{pnlPct}%
                  </p>
                  <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>
                    {getDuration() ? `Held ${getDuration()}` : '—'}
                  </p>
                </>
              ) : (
                <p className="text-xs font-bold" style={{ color: colors.secondaryText }}>—</p>
              )}
            </div>

            {rrRatio !== null ? (
              <div>
                <p className="text-[12px] uppercase tracking-wider font-semibold mb-1"
                   style={{ color: colors.secondaryText }}>Risk / Reward</p>
                <p className="text-xs font-bold" style={{ color: colors.primaryText }}>
                  1 : {rrRatio.toFixed(1)}
                </p>
                <p className="text-[12px] mt-0.5">
                  <span style={{ color: '#ef4444' }}>SL {fmtPrice(stopLoss)}</span>
                  <span style={{ color: colors.secondaryText }}> · </span>
                  <span style={{ color: '#16C784' }}>Tgt {fmtPrice(targetPrice)}</span>
                </p>
              </div>
            ) : (stopLoss != null || targetPrice != null) ? (
              <div>
                {stopLoss != null && (
                  <>
                    <p className="text-[12px] uppercase tracking-wider font-semibold"
                       style={{ color: '#ef4444' }}>Stop Loss</p>
                    <p className="text-xs font-bold mb-1" style={{ color: '#ef4444' }}>{fmtPrice(stopLoss)}</p>
                  </>
                )}
                {targetPrice != null && (
                  <>
                    <p className="text-[12px] uppercase tracking-wider font-semibold"
                       style={{ color: '#16C784' }}>Target</p>
                    <p className="text-xs font-bold" style={{ color: '#16C784' }}>{fmtPrice(targetPrice)}</p>
                  </>
                )}
              </div>
            ) : (
              <div>
                <p className="text-[12px] uppercase tracking-wider font-semibold mb-1"
                   style={{ color: colors.secondaryText }}>Duration</p>
                <p className="text-xs font-bold" style={{ color: colors.primaryText }}>
                  {getDuration() ?? '—'}
                </p>
                <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>
                  {isIntraday ? 'Intraday' : 'Swing'}
                </p>
              </div>
            )}
          </>)}

        </div>
      </div>
    </div>
  );
};

export default TradeReplay;
